/**
 * palette.js — adaptive brand palette for the ClientWave360 agent landing page.
 *
 * Ported from the redesign prototype's `theme.js`. Derives a full tonal ladder
 * from the corporation's brand colors (color_1 / color_2) in OKLCH so that any
 * input color yields a dignified, accessible palette. Raw brand colors are never
 * painted onto large surfaces directly — the deep "ink" surfaces, the AA-clamped
 * accent, and the tint washes are all computed.
 *
 * ClientWave360 defaults to green when a corporation has no brand color set.
 */

/** ClientWave360 brand green — used when a corporation has no color_1 configured. */
export const CF360_DEFAULT_GREEN = '#2ba453';

/* ---------- sRGB <-> OKLab/OKLCH ---------- */

function hexToRgb(hex) {
  if (typeof hex !== 'string') return null;
  let h = hex.trim().replace(/^#/, '');
  if (/^[0-9a-f]{3}$/i.test(h)) {
    h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
  }
  if (!/^[0-9a-f]{6}$/i.test(h)) return null;
  return {
    r: parseInt(h.slice(0, 2), 16) / 255,
    g: parseInt(h.slice(2, 4), 16) / 255,
    b: parseInt(h.slice(4, 6), 16) / 255
  };
}

function srgbToLinear(c) {
  return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}

function linearToSrgb(c) {
  return c <= 0.0031308 ? 12.92 * c : 1.055 * Math.pow(c, 1 / 2.4) - 0.055;
}

function rgbToOklch(rgb) {
  const r = srgbToLinear(rgb.r);
  const g = srgbToLinear(rgb.g);
  const b = srgbToLinear(rgb.b);
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
  const L = 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s;
  const a = 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s;
  const bb = 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s;
  const C = Math.sqrt(a * a + bb * bb);
  let h = (Math.atan2(bb, a) * 180) / Math.PI;
  if (h < 0) h += 360;
  return { L, C, h };
}

function oklchToRgbRaw(L, C, h) {
  const rad = (h * Math.PI) / 180;
  const a = C * Math.cos(rad);
  const b = C * Math.sin(rad);
  let l = L + 0.3963377774 * a + 0.2158037573 * b;
  let m = L - 0.1055613458 * a - 0.0638541728 * b;
  let s = L - 0.0894841775 * a - 1.291485548 * b;
  l = l * l * l;
  m = m * m * m;
  s = s * s * s;
  return {
    r: 4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    g: -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    b: -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s
  };
}

function inGamut(rgb) {
  return rgb.r >= -1e-4 && rgb.r <= 1.0001 && rgb.g >= -1e-4 && rgb.g <= 1.0001 && rgb.b >= -1e-4 && rgb.b <= 1.0001;
}

/** Convert OKLCH to a CSS rgb() string, reducing chroma until inside sRGB gamut. */
function oklch(L, C, h) {
  let lo = 0;
  let hi = C;
  let rgb = oklchToRgbRaw(L, C, h);
  if (!inGamut(rgb)) {
    for (let i = 0; i < 20; i++) {
      const mid = (lo + hi) / 2;
      rgb = oklchToRgbRaw(L, mid, h);
      if (inGamut(rgb)) lo = mid;
      else hi = mid;
    }
    rgb = oklchToRgbRaw(L, lo, h);
  }
  const to255 = c => Math.round(Math.min(1, Math.max(0, linearToSrgb(Math.min(1, Math.max(0, c))))) * 255);
  return `rgb(${to255(rgb.r)}, ${to255(rgb.g)}, ${to255(rgb.b)})`;
}

/* ---------- WCAG contrast ---------- */

function relativeLuminance(cssRgb) {
  const m = cssRgb.match(/rgb\((\d+), (\d+), (\d+)\)/);
  const r = srgbToLinear(m[1] / 255);
  const g = srgbToLinear(m[2] / 255);
  const b = srgbToLinear(m[3] / 255);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

function contrastOnWhite(cssRgb) {
  return 1.05 / (relativeLuminance(cssRgb) + 0.05);
}

/** Lower OKLCH lightness until the color meets the target contrast on white. */
function clampForContrast(L, C, h, target) {
  let lightness = L;
  let css = oklch(lightness, C, h);
  while (contrastOnWhite(css) < target && lightness > 0.18) {
    lightness -= 0.01;
    css = oklch(lightness, C, h);
  }
  return css;
}

/* ---------- Palette ---------- */

const NEUTRAL = { L: 0.3, C: 0.012, h: 255 }; // dignified charcoal fallback

/** Fixed light-azure tertiary background from the REQUEST-74 design key. */
export const CF360_TERTIARY_BG = '#f1f5f9';

function brandSource(hex) {
  const rgb = hexToRgb(hex);
  if (!rgb) return NEUTRAL;
  const lch = rgbToOklch(rgb);
  // Near-achromatic input (grays, near-black/white): treat as neutral.
  if (lch.C < 0.015) return { L: lch.L, C: lch.C, h: NEUTRAL.h };
  return lch;
}

/**
 * Build the CSS custom-property map from corporation colors.
 * color_1 (primary) drives buttons, icons, eyebrows, and links; color_2
 * (secondary) drives the hero/footer gradient toward charcoal (#141414);
 * color_3 (tertiary) is used as a section background only when it is light
 * enough to carry body text — otherwise the design key's light azure is used.
 * ClientWave360 defaults to green when color_1 is missing/invalid.
 *
 * @param {string | null | undefined} color1
 * @param {string | null | undefined} color2
 * @param {string | null | undefined} color3
 * @returns {Record<string, string>} map of CSS custom properties to values
 */
export function buildPalette(color1, color2, color3) {
  const b = brandSource(hexToRgb(color1) ? color1 : CF360_DEFAULT_GREEN);
  const C = b.C;
  const h = b.h;

  const vars = {
    // Deep surfaces — the brand hue at near-black lightness.
    '--brand-ink': oklch(0.225, Math.min(C, 0.062), h),
    '--brand-ink-deep': oklch(0.185, Math.min(C, 0.055), h),
    '--brand-ink-soft': oklch(0.31, Math.min(C, 0.07), h),
    // Interactive accent — brand color clamped to AA contrast on white.
    '--brand-accent': clampForContrast(Math.min(b.L, 0.55), Math.min(C, 0.155), h, 4.5),
    '--brand-accent-hover': clampForContrast(Math.min(b.L, 0.47), Math.min(C, 0.155), h, 5.4),
    // Accent for text on the dark hero/footer gradient — lifted for contrast on ink.
    '--brand-accent-bright': oklch(Math.max(b.L, 0.68), Math.min(C, 0.17), h),
    // Washes and lines.
    '--brand-wash': oklch(0.977, Math.min(C, 0.012), h),
    '--brand-tint': oklch(0.95, Math.min(C, 0.03), h),
    '--brand-line': oklch(0.895, Math.min(C, 0.022), h),
    // Text ramp, faintly warmed by the brand hue.
    '--text-strong': oklch(0.24, Math.min(C, 0.018), h),
    '--text-body': oklch(0.36, Math.min(C, 0.015), h),
    '--text-muted': oklch(0.52, Math.min(C, 0.012), h),
    // On-ink (hero/footer) text.
    '--on-ink': '#ffffff',
    '--on-ink-muted': oklch(0.84, Math.min(C, 0.035), h),
    '--on-ink-faint': oklch(0.66, Math.min(C, 0.045), h),
    '--on-ink-line': oklch(0.36, Math.min(C, 0.05), h)
  };

  const s = brandSource(color2);
  vars['--brand-secondary'] =
    s === NEUTRAL ? vars['--brand-accent'] : clampForContrast(Math.min(s.L, 0.55), Math.min(s.C, 0.14), s.h, 4.5);

  // Hero/footer gradient — the secondary color fading into charcoal (#141414),
  // per the REQUEST-74 design key. Falls back to the primary hue's ink family.
  const g = s === NEUTRAL ? b : s;
  vars['--grad-start'] = oklch(Math.min(Math.max(g.L, 0.32), 0.48), Math.min(g.C, 0.17), g.h);
  vars['--grad-mid'] = oklch(0.26, Math.min(g.C, 0.12), g.h);
  vars['--grad-end'] = '#141414';

  // Tertiary section background — the corporation's color_3 when it is a light,
  // text-safe wash; the design key's light azure otherwise.
  const t = hexToRgb(color3);
  const lightEnough = t && rgbToOklch(t).L > 0.9 && rgbToOklch(t).C < 0.05;
  vars['--brand-tertiary'] = lightEnough ? color3 : CF360_TERTIARY_BG;

  return vars;
}
